import React, { useState, useEffect, useCallback, useRef } from 'react';
import { trackEvent } from '../utils/analytics';
import type { OnboardingStep as OnboardingStepType } from '../types/ux-remediation';
import { resolveOnboardingCopy } from '../content/onboardingCopy';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface OnboardingProps {
  onboardingCompleted: boolean;
  onOnboardingComplete: () => void;
  onOnboardingRestart: () => void;
  sampleCase: Record<string, unknown>;
  steps: OnboardingStepType[];
  skippable?: boolean;
}

// ─── Focus-trap helper ───────────────────────────────────────────────────────

const FOCUSABLE_SELECTORS = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function trapFocus(container: HTMLElement, focusTarget?: HTMLElement) {
  const focusables = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
  );
  const first = focusables[0];
  const last = focusables[focusables.length - 1];

  const handler = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  };

  container.addEventListener('keydown', handler);
  if (focusTarget) {
    focusTarget.focus();
  } else {
    first?.focus();
  }
  return () => container.removeEventListener('keydown', handler);
}

// ─── Swipe detection helper ──────────────────────────────────────────────────

function useSwipe(
  onSwipeLeft: (duration?: number) => void,
  onSwipeRight: (duration?: number) => void,
  threshold = 50
) {
  const startX = useRef<number | null>(null);
  const startTime = useRef<number | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    startTime.current = Date.now();
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (startX.current === null || startTime.current === null) return;
      const diff = startX.current - e.changedTouches[0].clientX;
      const timeDiff = Date.now() - startTime.current;
      if (Math.abs(diff) > threshold && timeDiff > 0) {
        const velocity = Math.abs(diff) / timeDiff;
        const duration = velocity > 0.5 ? 150 : 300;
        if (diff > 0) onSwipeLeft(duration);
        else onSwipeRight(duration);
      }
      startX.current = null;
      startTime.current = null;
    },
    [onSwipeLeft, onSwipeRight, threshold]
  );

  return { onTouchStart, onTouchEnd };
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function Onboarding({
  onboardingCompleted,
  onOnboardingComplete,
  onOnboardingRestart,
  sampleCase,
  steps,
  skippable = true,
}: OnboardingProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isOpen, setIsOpen] = useState(!onboardingCompleted);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [announcedStep, setAnnouncedStep] = useState(0);
  const [swipeDuration, setSwipeDuration] = useState<number | undefined>(undefined);
  const [preOverflows, setPreOverflows] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const keyboardHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preRef = useRef<HTMLPreElement>(null);
  const dotRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
    }
  }, [isOpen]);

  // Validate steps
  if (!steps || steps.length === 0) {
    console.warn('[Onboarding] No steps provided');
    return null;
  }

  const stepCount = steps.length;
  const step = steps[currentStep];

  // ─── Sync isOpen with prop changes ───────────────────────────────────────────
  useEffect(() => {
    setIsOpen(!onboardingCompleted);
  }, [onboardingCompleted]);

  // ─── Focus trap ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const heading = modalRef.current.querySelector<HTMLElement>(
        `#onboarding-title-${currentStep}`
      );
      return trapFocus(modalRef.current, heading ?? undefined);
    }
  }, [isOpen]); // Intentionally not dependent on currentStep — regression fix from DEV-007

  // ─── Navigation ────────────────────────────────────────────────────────────
  const goToStep = (targetStep: number, direction: 'left' | 'right', duration?: number) => {
    if (targetStep < 0 || targetStep >= stepCount || targetStep === currentStep) return;

    if (reducedMotion) {
      setCurrentStep(targetStep);
      setAnnouncedStep(targetStep);
      return;
    }

    // Cancel any pending animation
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setSlideDirection(direction);
    setCurrentStep(targetStep);
    if (duration) setSwipeDuration(duration);

    const animDuration = duration ?? 300;
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setSlideDirection(null);
      setAnnouncedStep(targetStep);
      setSwipeDuration(undefined);
      modalRef.current
        ?.querySelector<HTMLElement>(`#onboarding-title-${targetStep}`)
        ?.focus();
    }, animDuration);
  };

  const handleNext = useCallback(() => {
    if (currentStep < stepCount - 1) {
      goToStep(currentStep + 1, 'left');
    } else {
      trackEvent('onboarding_complete', { stepCount });
      setIsOpen(false);
      onOnboardingComplete();
      previousFocusRef.current?.focus();
    }
  }, [currentStep, stepCount, onOnboardingComplete]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      goToStep(currentStep - 1, 'right');
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    trackEvent('onboarding_skip', { step: currentStep + 1 });
    setIsOpen(false);
    onOnboardingComplete();
    previousFocusRef.current?.focus();
  }, [currentStep, onOnboardingComplete]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (currentStep === 0 && !skippable) return;
      trackEvent('onboarding_dismissed_backdrop', { step: currentStep + 1 });
      handleSkip();
    },
    [currentStep, skippable, handleSkip]
  );

  const handleRestart = useCallback(() => {
    trackEvent('onboarding_restart', {});
    setCurrentStep(0);
    setAnnouncedStep(0);
    setSlideDirection(null);
    setIsOpen(true);
    onOnboardingRestart();
  }, [onOnboardingRestart]);

  // ─── Keyboard navigation (ref-based, no churn) ─────────────────────────────
  keyboardHandlerRef.current = (e: KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'ArrowLeft') handlePrev();
    if (e.key === 'Escape') handleSkip();
  };

  useEffect(() => {
    const listener = (e: KeyboardEvent) => keyboardHandlerRef.current(e);
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, []); // Empty deps — listener attached once, never churns on re-render

  // ─── Analytics on step view ────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && step) {
      trackEvent(`onboarding_step_${currentStep + 1}`, {
        stepId: step.id,
        stepTitle: step.title,
      });
    }
  }, [isOpen, currentStep, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Pre block overflow detection ──────────────────────────────────────────
  useEffect(() => {
    if (preRef.current) {
      setPreOverflows(preRef.current.scrollWidth > preRef.current.clientWidth);
    }
  }, [sampleCase, currentStep]);

  // ─── Cleanup on unmount ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // ─── Swipe handlers ────────────────────────────────────────────────────────
  const { onTouchStart, onTouchEnd } = useSwipe(
    (duration) => goToStep(currentStep + 1, 'left', duration),
    (duration) => goToStep(currentStep - 1, 'right', duration)
  );

  // ─── Dot keyboard navigation ───────────────────────────────────────────────
  const handleDotKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = dotRefs.current[idx + 1];
      next?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = dotRefs.current[idx - 1];
      prev?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToStep(idx, idx > currentStep ? 'left' : 'right');
    }
  };

  // ─── Dot indicators ────────────────────────────────────────────────────────
  const renderDots = () => (
    <div className="flex items-center justify-center gap-2 py-2">
      {steps.map((s, idx) => (
        <button
          key={s.id}
          ref={(el) => {
            dotRefs.current[idx] = el;
          }}
          onClick={() => goToStep(idx, idx > currentStep ? 'left' : 'right')}
          onKeyDown={(e) => handleDotKeyDown(e, idx)}
          className="min-h-[44px] min-w-[44px] p-0 border-0 bg-transparent transition-colors"
          aria-label={`Step ${idx + 1} of ${stepCount}`}
          aria-current={idx === currentStep ? 'step' : undefined}
          tabIndex={0}
          type="button"
        >
          <span
            className={`block w-2 h-2 rounded-none mx-auto ${
              idx === currentStep ? 'bg-navy-900' : 'bg-paper-300'
            }`}
          />
        </button>
      ))}
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────────
  if (!isOpen) return null;

  const transitionClass = reducedMotion
    ? ''
    : 'transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]';

  const renderSlideContent = (idx: number) => {
    const s = steps[idx];
    const isSampleCase = s.id === 'sample-case';
    return (
      <div
        className="w-full h-full shrink-0 px-6 py-6 overflow-y-auto flex flex-col"
        {...(idx !== announcedStep ? { inert: '', 'aria-hidden': true } : {})}
      >
        <h2
          id={`onboarding-title-${idx}`}
          tabIndex={-1}
          className="font-serif text-3xl font-bold text-navy-900 mb-4"
        >
          {s.title}
        </h2>

        <div id={`onboarding-desc-${idx}`} className="space-y-4">
          {typeof s.content === 'string' ? (
            <p className="text-sm text-paper-700 leading-relaxed">{s.content}</p>
          ) : (
            s.content
          )}
        </div>

        {isSampleCase && sampleCase && (
          <div className="mt-4 p-4 bg-white border border-paper-300 rounded-none">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-1 text-xs font-medium bg-paper-100 text-navy-900 border border-paper-300">
                Criminal Defense
              </span>
              <span className="text-xs text-paper-500">Defense</span>
            </div>
            <h3 className="font-semibold text-navy-900 mb-1">
              State v. Rodriguez
            </h3>
            <p className="text-sm text-paper-700 leading-relaxed">
              Client pulled over for lane drifting. Breathalyzer read .09, but the officer had not calibrated the device in 14 months.
            </p>
            <div className="mt-3 flex items-center gap-4 text-xs text-paper-500">
              <span>12 jurors</span>
              <span>Urban working-class venue</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`onboarding-title-${announcedStep}`}
      aria-describedby={`onboarding-desc-${announcedStep}`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-none border border-paper-300 shadow-sm w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Live region for step announcements */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          Step {announcedStep + 1} of {stepCount}: {steps[announcedStep].title}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-paper-300 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-paper-500 uppercase tracking-wider">
              Step {currentStep + 1} of {stepCount}
            </span>
          </div>
          <button
            onClick={handleSkip}
            className="text-sm font-medium text-paper-600 hover:text-navy-900 px-3 py-2 transition-colors min-h-[44px]"
            type="button"
          >
            {resolveOnboardingCopy('ACTION_SKIP')}
          </button>
        </div>

        {/* Progress bar */}
        <div
          className="w-full bg-paper-100 h-1 shrink-0"
          role="progressbar"
          aria-valuenow={currentStep + 1}
          aria-valuemin={1}
          aria-valuemax={stepCount}
          aria-label="Onboarding progress"
        >
          <div
            className={`bg-navy-900 h-1 ${reducedMotion ? '' : 'transition-all duration-300'}`}
            style={{ width: `${((currentStep + 1) / stepCount) * 100}%` }}
          />
        </div>

        {/* Content carousel */}
        {/* min-h-0 is load-bearing: without it the flex child refuses to
            shrink below content height, the modal overflows 90vh, and the
            slide's overflow-y-auto never engages (content visibly cut off). */}
        <div id="onboarding-body" className="flex-1 min-h-0 overflow-hidden relative">
          <div
            className={`flex h-full ${transitionClass}`}
            style={{
              transform: `translateX(-${currentStep * 100}%)`,
              transitionDuration: swipeDuration ? `${swipeDuration}ms` : undefined,
            }}
          >
            {steps.map((s, idx) => (
              <div key={s.id} className="w-full h-full shrink-0 overflow-hidden">
                {renderSlideContent(idx)}
              </div>
            ))}
          </div>
        </div>

        {/* Dot indicators */}
        {renderDots()}

        {/* Swipe indicator (mobile only) */}
        <div className="text-center pb-2 md:hidden">
          <span className="text-xs text-paper-500">Swipe to navigate</span>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-paper-300 shrink-0">
          <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="inline-flex items-center px-5 py-3 text-sm font-medium border border-paper-300 text-navy-900 hover:bg-paper-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors min-h-[44px]"
            type="button"
          >
            {resolveOnboardingCopy('ACTION_PREVIOUS')}
          </button>
          <div className="flex items-center gap-2">
            {step.secondaryCta && (
              <button
                onClick={handleSkip}
                className="inline-flex items-center px-5 py-3 text-sm font-medium border border-paper-300 text-navy-900 hover:bg-paper-50 transition-colors min-h-[44px]"
                type="button"
              >
                {step.secondaryCta}
              </button>
            )}
            <button
              onClick={handleNext}
              className="inline-flex items-center px-5 py-3 text-sm font-medium bg-navy-900 text-white hover:bg-navy-800 transition-colors min-h-[44px]"
              type="button"
            >
              {step.cta ||
                (currentStep < stepCount - 1
                  ? resolveOnboardingCopy('ACTION_NEXT')
                  : resolveOnboardingCopy('ACTION_GET_STARTED'))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Re-export for convenience
export { useSwipe };
